import { AfterViewInit, Component, ElementRef, HostBinding, ViewEncapsulation, input, inject, signal } from '@angular/core';
import { NgClass } from '@angular/common';

import { ApIconComponent } from '@ap/icon';

import { SpinnerReadyIconSize, SpinnerSize, SpinnerState } from './spinner.model';

@Component({
    selector: 'ap-spinner',
    templateUrl: './spinner.component.html',
    styleUrls: ['./spinner.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [ApIconComponent, NgClass]
})
export class ApSpinnerComponent implements AfterViewInit {
    private readonly elementRef = inject(ElementRef);

    /**
     * State of the spinner
     * Possible values are: 'idle' | 'loading' | 'completed' | 'failed';
     * Defaults to idle
     */
    public readonly state = input<SpinnerState>('idle');

    /**
     * Size of the spinner.
     * Possible values are: `medium` | `small` | `xsmall` | `dynamic`
     * Defaults to `medium`
     */
    public readonly size = input<SpinnerSize>('medium');

    /**
     * Size of the icon when completed or failed
     * Possible values are: 'small' | 'default'
     * Defaults to `default`
     */
    public readonly readyIconSize = input<SpinnerReadyIconSize>('default');

    /**
     * Text to show while loading
     * For A11Y purposes it should be required.
     */
    public readonly loadingText = input.required<string>();

    /**
     * Text to show when spinner is ready
     * For A11Y purposes it should be required.
     */
    public readonly readyText = input.required<string>();

    /**
     * Text to show when spinner loading is failed
     */
    public readonly failedText = input<string>(undefined);

    /**
     * Hide label
     */
    public readonly visuallyHiddenLabel = input(false);

    /**
     * Show spinner icon?
     * Defaults to true
     */
    public readonly icon = input(true);

    protected text = signal<string>('');

    @HostBinding('style.height') protected elementHeight = '100%';

    protected getClasses(): string[] {
        const classes = [];

        const readyIconSize = this.readyIconSize();
        classes.push(readyIconSize !== 'default' && this.state() !== 'loading' ? readyIconSize : this.size());
        return classes;
    }

    public ngAfterViewInit(): void {
        if (this.size() === 'dynamic') {
            const parentElement = this.elementRef?.nativeElement?.parentElement || null;
            if (parentElement === null) {
                this.elementHeight = '100%';
                return;
            }
            const style = window.getComputedStyle(parentElement);
            const lineHeight = style.getPropertyValue('line-height');
            this.elementHeight = lineHeight ? lineHeight : `${parentElement.offsetHeight}px`;
        }

        /**
         * Workaround to make sure the loadingText is read by aria-live polite.
         * If text is rendered immediately then aria-live won't read.
         */
        setTimeout(() => {
            this.text.set(this.loadingText());
        }, 50);
    }
}
